import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { GasNetworkTariffModel, TrailerModel } from '@core/api';
import { finalize } from 'rxjs/operators';
import { StationService } from '@accounts/user/services/station.service';

const HOURS_MAP = {
  [GasNetworkTariffModel.TypeEnum.FixedHours]: 1,
  [GasNetworkTariffModel.TypeEnum.Daily]: 24
};

export interface CustomTariff {
  value: number;
  type: GasNetworkTariffModel.TypeEnum;
}

@Injectable({
  providedIn: 'root'
})
export class TariffService {

  private trailerSubject = new BehaviorSubject<TrailerModel>(null);
  public trailer$ = this.trailerSubject.asObservable();

  private tariffSubject = new BehaviorSubject<GasNetworkTariffModel>(null);
  public tariff$ = this.tariffSubject.asObservable();

  private totalPriceSubject = new BehaviorSubject<number>(0);
  public totalPrice$ = this.totalPriceSubject.asObservable();

  private tariffLoadingSubject = new BehaviorSubject<boolean>(false);
  public tariffLoading$ = this.tariffLoadingSubject.asObservable();

  private customTariffSubject = new BehaviorSubject<CustomTariff>({
    value: 24,
    type: GasNetworkTariffModel.TypeEnum.Daily
  });
  public customTariff$ = this.customTariffSubject.asObservable();

  constructor(
    private stationService: StationService
  ) { }

  get tariffValue(): number {
    return Math.max(this.customTariff.value, this.tariff.min_rent_hours);
  }

  set trailer(value: TrailerModel) {
    this.trailerSubject.next(value);
  }

  get trailer(): TrailerModel {
    return this.trailerSubject.value;
  }

  set tariff(value: GasNetworkTariffModel) {
    this.tariffSubject.next(value);
  }

  get tariff(): GasNetworkTariffModel {
    return this.tariffSubject.value;
  }

  get tariffLoading() {
    return this.tariffLoadingSubject.value;
  }

  set customTariff(value: CustomTariff) {
    this.customTariffSubject.next(value);
  }

  get customTariff(): CustomTariff {
    return this.customTariffSubject.value;
  }

  get totalPrice(): number {
    return this.totalPriceSubject.value;
  }

  changeCurrentTariff(value: number) {
    this.tariffLoadingSubject.next(true);

    return this.stationService.getBestTariff(this.stationService.station.network_id, value)
      .pipe(finalize(() => this.tariffLoadingSubject.next(false)))
      .subscribe(res => {
        this.tariff = res.data;

        this.customTariffSubject.next({
          value: value,
          type: this.tariff.type
        });

        this.totalPriceSubject.next(res.meta.total);
      });
  }

  reset() {
    this.tariff = null;
    this.totalPriceSubject.next(null);
  }
}
